
import { computed, defineComponent, nextTick, PropType, reactive, ref, watch } from 'vue'
import { ElMessage } from 'element-plus'
import dayjs from 'dayjs'
import {
  getPageListData,
  getPostPageListData
} from '@/service/main/system'

interface TableColumn {
  prop: string
  label: string
  width?: string | number
  minWidth?: string | number
  type?: 'text' | 'avatar' | 'image'
  imgSize?: number
}

interface SearchDefaultConfig {
  /** 预设：today / yesterday / thisWeek / thisMonth / last7days / last30days */
  preset?: string
  /** 近 N 天（含今天），等价 startOffset=-(N-1), endOffset=0 */
  days?: number
  /** 相对今天的起始偏移（天），如 -6 表示 6 天前 */
  startOffset?: number
  /** 相对今天的结束偏移（天），如 0 表示今天 */
  endOffset?: number
  /** 偏移单位，默认 day */
  unit?: 'day' | 'week' | 'month'
  /** 固定开始日期 YYYY-MM-DD */
  start?: string
  /** 固定结束日期 YYYY-MM-DD */
  end?: string
  /** 静态值（输入框或直接区间） */
  value?: string | number | string[] | null
}

interface SearchColumn {
  prop: string
  label: string
  type?: 'input' | 'daterange' | 'datetimerange'
  placeholder?: string
  startPlaceholder?: string
  endPlaceholder?: string
  valueFormat?: string
  beginKey?: string
  endKey?: string
  width?: string | number
  /**
   * 默认值，支持：
   * 1) 字符串预设：today / yesterday / last7days / last15days / thisWeek / thisMonth
   * 2) 数组：["2026-01-01","2026-01-07"]
   * 3) 对象：{ days: 7 } / { startOffset: -6, endOffset: 0 } / { start, end } / { preset }
   */
  default?: string | string[] | number | SearchDefaultConfig | null
}

export default defineComponent({
  name: 'ElTableExt',
  props: {
    modelValue: {
      type: [String, Number] as PropType<string | number | null>,
      default: null
    },
    formModel: {
      type: Object as PropType<Record<string, any>>,
      default: null
    },
    dataSource: {
      type: String,
      default: ''
    },
    requestMethod: {
      type: String as PropType<'get' | 'post'>,
      default: 'get'
    },
    valueKey: {
      type: String,
      default: 'id'
    },
    jsonField: {
      type: String,
      default: ''
    },
    columns: {
      type: Array as PropType<TableColumn[]>,
      default: () => []
    },
    searchColumns: {
      type: Array as PropType<SearchColumn[]>,
      default: () => []
    },
    // 选中行字段 → 表单字段映射，如 { "customer_name": "customerName", "fee": "paymentAmount" }
    fillMapping: {
      type: Object as PropType<Record<string, string>>,
      default: () => ({})
    },
    // input: 文本框回显 id；record: 当前字段内回显一行记录
    displayMode: {
      type: String as PropType<'input' | 'record'>,
      default: 'input'
    },
    // 记录回显展示哪些字段；不配则默认用 columns 前几项
    displayColumns: {
      type: Array as PropType<TableColumn[]>,
      default: () => []
    },
    listKey: {
      type: String,
      default: 'list'
    },
    totalKey: {
      type: String,
      default: 'total'
    },
    pageNoKey: {
      type: String,
      default: 'pageNo'
    },
    pageSizeKey: {
      type: String,
      default: 'pageSize'
    },
    placeholder: {
      type: String,
      default: '请选择'
    },
    clearable: {
      type: Boolean,
      default: false
    },
    dialogTitle: {
      type: String,
      default: '请选择'
    }
  },
  emits: ['update:modelValue', 'change'],
  setup(props, { emit }) {
    const dialogVisible = ref(false)
    const loading = ref(false)
    const tableData = ref<any[]>([])
    const total = ref(0)
    const pageNo = ref(1)
    const pageSize = ref(10)
    const currentRow = ref<any>(null)
    const searchForm = reactive<Record<string, any>>({})
    const selectedRecord = ref<Record<string, any> | null>(null)
    const dialogTableRef = ref<any>(null)

    const isRecordDisplay = computed(() => props.displayMode === 'record')

    const effectiveDisplayColumns = computed(() => {
      if (props.displayColumns && props.displayColumns.length > 0) {
        return props.displayColumns
      }
      return (props.columns || []).slice(0, 5)
    })

    const selectedRecordList = computed(() => {
      return selectedRecord.value ? [selectedRecord.value] : []
    })

    const displayValue = computed(() => {
      if (props.modelValue === null || props.modelValue === undefined) {
        return ''
      }
      return String(props.modelValue)
    })

    const parseSelectedRecord = () => {
      if (!props.jsonField || !props.formModel) {
        selectedRecord.value = null
        return
      }
      const raw = props.formModel[props.jsonField]
      if (!raw) {
        selectedRecord.value = null
        return
      }
      try {
        const parsed = typeof raw === 'string' ? JSON.parse(raw) : raw
        selectedRecord.value =
          parsed && typeof parsed === 'object' ? parsed : null
      } catch (e) {
        selectedRecord.value = null
      }
    }

    watch(
      () =>
        props.formModel && props.jsonField
          ? props.formModel[props.jsonField]
          : null,
      () => {
        parseSelectedRecord()
      },
      { immediate: true }
    )

    const resetSearchForm = () => {
      Object.keys(searchForm).forEach((key) => {
        delete searchForm[key]
      })
      ;(props.searchColumns || []).forEach((item) => {
        searchForm[item.prop] = resolveSearchDefault(item)
      })
    }

    const resolveSearchDefault = (item: SearchColumn) => {
      const format =
        item.valueFormat ||
        (item.type === 'datetimerange' ? 'YYYY-MM-DD HH:mm:ss' : 'YYYY-MM-DD')
      const isRange =
        item.type === 'daterange' || item.type === 'datetimerange'
      const isDateTime = item.type === 'datetimerange'
      const def = item.default

      if (def === undefined || def === null || def === '') {
        return isRange ? null : ''
      }

      if (Array.isArray(def)) {
        return def
      }

      const formatRange = (start: dayjs.Dayjs, end: dayjs.Dayjs) => {
        if (isDateTime) {
          return [
            start.startOf('day').format(format),
            end.endOf('day').format(format)
          ]
        }
        return [start.format(format), end.format(format)]
      }

      const fromPreset = (presetRaw: string) => {
        const key = presetRaw.toLowerCase().trim()
        const now = dayjs()

        if (key === 'today') {
          if (isRange) {
            return formatRange(now, now)
          }
          return now.format(format)
        }
        if (key === 'yesterday') {
          const day = now.subtract(1, 'day')
          if (isRange) {
            return formatRange(day, day)
          }
          return day.format(format)
        }
        if (key === 'thisweek' || key === 'this_week') {
          return formatRange(now.startOf('week'), now.endOf('week'))
        }
        if (key === 'thismonth' || key === 'this_month') {
          return formatRange(now.startOf('month'), now.endOf('month'))
        }
        if (key === 'last7days' || key === 'last7day') {
          return formatRange(now.subtract(6, 'day'), now)
        }
        if (key === 'last30days' || key === 'last30day') {
          return formatRange(now.subtract(29, 'day'), now)
        }
        const lastDaysMatch = key.match(/^last(\d+)days?$/)
        if (lastDaysMatch) {
          const days = Math.max(Number(lastDaysMatch[1]) || 1, 1)
          return formatRange(now.subtract(days - 1, 'day'), now)
        }
        return null
      }

      const fromConfig = (cfg: SearchDefaultConfig) => {
        if (cfg.value !== undefined) {
          return cfg.value
        }
        if (cfg.preset) {
          const presetVal = fromPreset(cfg.preset)
          if (presetVal !== null) {
            return presetVal
          }
        }
        if (cfg.start || cfg.end) {
          const start = dayjs(cfg.start || cfg.end)
          const end = dayjs(cfg.end || cfg.start)
          return formatRange(start, end)
        }
        if (typeof cfg.days === 'number' && cfg.days > 0) {
          const end = dayjs()
          const start = dayjs().subtract(cfg.days - 1, 'day')
          return formatRange(start, end)
        }
        if (
          typeof cfg.startOffset === 'number' ||
          typeof cfg.endOffset === 'number'
        ) {
          const unit = cfg.unit || 'day'
          const start = dayjs().add(cfg.startOffset ?? 0, unit)
          const end = dayjs().add(cfg.endOffset ?? 0, unit)
          return formatRange(start, end)
        }
        return isRange ? null : ''
      }

      if (typeof def === 'string') {
        const presetVal = fromPreset(def)
        if (presetVal !== null) {
          return presetVal
        }
        return def
      }

      if (typeof def === 'object') {
        return fromConfig(def as SearchDefaultConfig)
      }

      return def
    }

    const buildSearchQuery = () => {
      const queryInfo: Record<string, any> = {}
      ;(props.searchColumns || []).forEach((item) => {
        const value = searchForm[item.prop]
        if (item.type === 'daterange' || item.type === 'datetimerange') {
          if (Array.isArray(value) && value.length === 2 && value[0] && value[1]) {
            const beginKey = item.beginKey || `${item.prop}_begin`
            const endKey = item.endKey || `${item.prop}_end`
            queryInfo[beginKey] = value[0]
            queryInfo[endKey] = value[1]
          }
          return
        }
        if (value !== undefined && value !== null && String(value).trim() !== '') {
          queryInfo[item.prop] = typeof value === 'string' ? value.trim() : value
        }
      })
      return queryInfo
    }

    const setJsonField = (value: string | null) => {
      if (!props.jsonField || !props.formModel) {
        return
      }
      props.formModel[props.jsonField] = value
    }

    const applyFillMapping = (row: Record<string, any> | null) => {
      if (!props.formModel || !props.fillMapping) {
        return
      }
      Object.keys(props.fillMapping).forEach((formField) => {
        const rowField = props.fillMapping[formField]
        if (!rowField) {
          return
        }
        if (!row) {
          props.formModel[formField] = null
          return
        }
        const value = resolvePath(row, rowField)
        props.formModel[formField] =
          value === undefined ? null : value
      })
    }

    const emitValue = (value: string | number | null) => {
      emit('update:modelValue', value)
      emit('change', value)
    }

    const handleClear = () => {
      if (!props.clearable) {
        return
      }
      selectedRecord.value = null
      emitValue(null)
      setJsonField(null)
      applyFillMapping(null)
    }

    const openDialog = () => {
      if (!props.dataSource) {
        ElMessage.error('未配置 dataSource，无法加载数据')
        return
      }
      if (!props.columns || props.columns.length === 0) {
        ElMessage.error('未配置 columns，无法展示表格')
        return
      }
      dialogVisible.value = true
    }

    const handleDialogOpen = () => {
      pageNo.value = 1
      currentRow.value = null
      resetSearchForm()
      loadTableData(true)
    }

    const handleDialogClosed = () => {
      currentRow.value = null
      tableData.value = []
      total.value = 0
      resetSearchForm()
    }

    const resolvePath = (data: any, path: string) => {
      if (!path) {
        return data
      }
      return path.split('.').reduce((acc: any, key: string) => {
        if (acc == null) {
          return undefined
        }
        return acc[key]
      }, data)
    }

    const restoreCurrentSelection = () => {
      const selectedValue =
        props.modelValue !== null &&
        props.modelValue !== undefined &&
        props.modelValue !== ''
          ? props.modelValue
          : selectedRecord.value
            ? selectedRecord.value[props.valueKey]
            : null
      if (
        selectedValue === null ||
        selectedValue === undefined ||
        selectedValue === ''
      ) {
        currentRow.value = null
        nextTick(() => {
          dialogTableRef.value?.setCurrentRow?.(null)
        })
        return
      }
      const matched = (tableData.value || []).find(
        (row: any) => String(row?.[props.valueKey]) === String(selectedValue)
      )
      currentRow.value = matched || null
      nextTick(() => {
        if (matched) {
          dialogTableRef.value?.setCurrentRow?.(matched)
        } else {
          dialogTableRef.value?.setCurrentRow?.(null)
        }
      })
    }

    const loadTableData = (restoreSelection = false) => {
      if (!props.dataSource) {
        return
      }
      loading.value = true
      const queryInfo: Record<string, any> = {
        [props.pageNoKey]: pageNo.value,
        [props.pageSizeKey]: pageSize.value,
        ...buildSearchQuery()
      }
      const request =
        props.requestMethod === 'post'
          ? getPostPageListData(props.dataSource, queryInfo)
          : getPageListData(props.dataSource, queryInfo)

      request
        .then((res: any) => {
          // 兼容：1) { list, total }  2) 拦截器已解包成数组  3) { data: [] }
          let list: any
          if (Array.isArray(res)) {
            list = res
          } else if (props.listKey) {
            list = resolvePath(res, props.listKey)
          } else {
            list = res
          }
          if (!Array.isArray(list) && res && Array.isArray(res.data)) {
            list = res.data
          }
          const totalVal = Array.isArray(res)
            ? res.length
            : resolvePath(res, props.totalKey)
          if (!Array.isArray(list)) {
            tableData.value = []
            total.value = 0
            ElMessage.warning('接口返回结构与 listKey 不匹配，已按空列表处理')
            return
          }
          tableData.value = list
          total.value = Number(totalVal) || list.length || 0
          if (restoreSelection) {
            restoreCurrentSelection()
          }
        })
        .catch(() => {
          tableData.value = []
          total.value = 0
          ElMessage.error('加载数据失败')
        })
        .finally(() => {
          loading.value = false
        })
    }

    const handleSearch = () => {
      pageNo.value = 1
      loadTableData(true)
    }

    const handleSearchReset = () => {
      resetSearchForm()
      pageNo.value = 1
      loadTableData(true)
    }

    const handleCurrentRowChange = (row: any) => {
      currentRow.value = row
    }

    const handleSizeChange = (size: number) => {
      pageSize.value = size
      pageNo.value = 1
      loadTableData(true)
    }

    const handlePageChange = (page: number) => {
      pageNo.value = page
      loadTableData(true)
    }

    const handleConfirm = () => {
      if (!currentRow.value) {
        ElMessage.warning('请先选择一条记录')
        return
      }
      const value = currentRow.value[props.valueKey]
      if (value === undefined || value === null || value === '') {
        ElMessage.warning(`选中行缺少字段：${props.valueKey}`)
        return
      }
      selectedRecord.value = { ...currentRow.value }
      emitValue(value)
      setJsonField(JSON.stringify(currentRow.value))
      applyFillMapping(currentRow.value)
      dialogVisible.value = false
    }

    return {
      dialogVisible,
      dialogTableRef,
      loading,
      tableData,
      total,
      pageNo,
      pageSize,
      searchForm,
      selectedRecord,
      selectedRecordList,
      isRecordDisplay,
      effectiveDisplayColumns,
      displayValue,
      openDialog,
      handleClear,
      handleDialogOpen,
      handleDialogClosed,
      handleSearch,
      handleSearchReset,
      handleCurrentRowChange,
      handleSizeChange,
      handlePageChange,
      handleConfirm
    }
  }
})
